// ⌘K command palette (phase 17): global search across clients, projects,
// tasks, invoices and leads, plus "New task: …" — Henry's chosen action set
// (search + task only, with a quick optional project pick). Modal-family
// styling, arrow-key navigation, Enter to go.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { displayCents, useMoneyMode } from '../lib/money';
import { createQuickTask } from '../lib/quickTask';
import { useIsMobile } from '../lib/useIsMobile';
import {
  formatInvoiceNumber,
  formatMoney,
  formatProjectNumber,
} from './primitives';
import { Sheet, SheetButton } from './Sheet';
import {
  DocumentsIcon,
  ClientsIcon,
  CrewIcon,
  FinanceIcon,
  LeadsIcon,
  ProjectsIcon,
  TasksIcon,
} from './icons';

interface SearchResults {
  knowledge?: { id:string; title:string; kind:string; category:string }[];
  clients: { id: string; name: string; contact_name: string | null }[];
  projects: { id: string; name: string; project_number: number | null; phase: string; client_name: string }[];
  tasks: { id: string; title: string; status: string; project_id: string | null; project_name: string | null }[];
  invoices: { id: string; invoice_number: string; status: string; amount_cents: number; gst_cents: number; client_name: string }[];
  leads: { id: string; name: string; company: string | null; status: string }[];
  crew: { id: string; name: string; role: string | null; company: string | null }[];
  expenses: { id: string; vendor: string | null; description: string | null; category: string; amount_cents: number; gst_cents: number; expense_date: string; project_id: string | null }[];
}

const EMPTY: SearchResults = { clients: [], projects: [], tasks: [], invoices: [], leads: [], crew: [], expenses: [] };

interface Item {
  key: string;
  group: string;
  icon: React.ReactNode;
  title: string;
  context?: string | null;
  meta?: React.ReactNode;
  run: () => void | Promise<void>;
}

export default function CommandPalette({
  open,
  task = false,
  onClose,
}: {
  open: boolean;
  /** ⌘E: open straight into new-task entry (Henry's ask, 2026-06-13). */
  task?: boolean;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults>(EMPTY);
  const [index, setIndex] = useState(0);
  // Quick project pick after "New task" (Henry's pick — optional 2nd step).
  const [taskTitle, setTaskTitle] = useState<string | null>(null);
  // "New task…" straight from the empty palette (Henry's ask) — the input
  // becomes the title field.
  const [composing, setComposing] = useState(false);
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const mode = useMoneyMode();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults(EMPTY);
      setIndex(0);
      setTaskTitle(null);
      setComposing(false);
      setNotice(null);
    } else {
      setComposing(task);
    }
  }, [open, task]);

  // Debounced search.
  useEffect(() => {
    if (!open || taskTitle !== null || composing) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults(EMPTY);
      setIndex(0);
      return;
    }
    const t = setTimeout(() => {
      api<SearchResults>(`/api/admin/search?q=${encodeURIComponent(q)}`)
        .then((r) => {
          setResults(r);
          setIndex(0);
        })
        .catch(() => {});
    }, 180);
    return () => clearTimeout(t);
  }, [query, open, taskTitle, composing]);

  // One POST, shared with the mobile quick-add sheet (Phase 0 §0.4.5).
  const createTask = useCallback(
    async (title: string, projectId: string | null) => {
      await createQuickTask(title, projectId);
      setNotice(`Task added — due today`);
      setTimeout(onClose, 650);
    },
    [onClose],
  );

  // Lead quick-capture (Phase 1.6): create with just a name and jump to the
  // detail page so Henry can fill source/company/etc. there — a fast path from
  // the palette without opening the New-lead modal.
  const createLead = useCallback(
    async (name: string) => {
      const res = await api<{ lead: { id: string } }>('/api/admin/leads', { method: 'POST', json: { name } }).catch(
        () => null,
      );
      if (res?.lead) {
        navigate(`/leads/${res.lead.id}`);
        onClose();
      }
    },
    [navigate, onClose],
  );

  const go = useCallback(
    (to: string) => {
      navigate(to);
      onClose();
    },
    [navigate, onClose],
  );

  const beginTask = useCallback(
    async (title: string) => {
      setTaskTitle(title);
      setQuery('');
      setIndex(0);
      if (projects.length === 0) {
        const d = await api<{ projects: { id: string; name: string; voided_at: number | null }[] }>(
          '/api/admin/projects',
        ).catch(() => null);
        // A new task can't land on a voided project — leave them off the list.
        if (d) setProjects(d.projects.filter((p) => p.voided_at === null));
      }
    },
    [projects.length],
  );

  // Flattened, ordered item list (or the project-pick list in step 2).
  const items: Item[] = useMemo(() => {
    if (taskTitle !== null) {
      const q = query.trim().toLowerCase();
      const visible = q
        ? projects.filter((p) => p.name.toLowerCase().includes(q))
        : projects;
      return [
        {
          key: 'no-project',
          group: 'NEW TASK',
          icon: <TasksIcon />,
          title: `Create “${taskTitle}” — no project`,
          meta: 'due today',
          run: () => createTask(taskTitle, null),
        },
        ...visible.slice(0, 8).map((p) => ({
          key: `proj-${p.id}`,
          group: 'LINK TO PROJECT',
          icon: <ProjectsIcon />,
          title: p.name,
          meta: 'due today',
          run: () => createTask(taskTitle, p.id),
        })),
      ];
    }

    // Compose mode (⌘E): the input IS the task title.
    if (composing) {
      const title = query.trim();
      if (!title) return [];
      return [
        {
          key: 'compose-create',
          group: 'NEW TASK',
          icon: <TasksIcon />,
          title: `New task: “${title}”`,
          meta: 'due today ↵',
          run: () => beginTask(title),
        },
      ];
    }

    const list: Item[] = [];
    for (const e of results.knowledge ?? []) list.push({key:`knowledge-${e.id}`,group:'SYSTEMS',icon:<DocumentsIcon />,title:e.title,context:e.category,meta:e.kind,run:()=>go(`/systems/${e.id}`)});
    for (const p of results.projects) {
      list.push({
        key: `p-${p.id}`,
        group: 'PROJECTS',
        icon: <ProjectsIcon />,
        title: p.name,
        context: p.client_name,
        meta: formatProjectNumber(p.project_number) ?? undefined,
        run: () => go(`/projects/${p.id}`),
      });
    }
    for (const t of results.tasks) {
      list.push({
        key: `t-${t.id}`,
        group: 'TASKS',
        icon: <TasksIcon />,
        title: t.title,
        context: t.project_name,
        meta: t.status === 'done' ? 'done' : undefined,
        run: () => go(t.project_id ? `/projects/${t.project_id}` : '/tasks'),
      });
    }
    for (const cl of results.clients) {
      list.push({
        key: `c-${cl.id}`,
        group: 'CLIENTS',
        icon: <ClientsIcon />,
        title: cl.name,
        context: cl.contact_name,
        run: () => go(`/clients/${cl.id}`),
      });
    }
    for (const inv of results.invoices) {
      list.push({
        key: `i-${inv.id}`,
        group: 'INVOICES',
        icon: <FinanceIcon />,
        title: `Invoice ${formatInvoiceNumber(inv.invoice_number)}`,
        context: inv.client_name,
        meta: (
          <span className="gold">{formatMoney(displayCents(inv.amount_cents, inv.gst_cents, mode))}</span>
        ),
        run: () => go(`/finance/invoices/${inv.id}`),
      });
    }
    for (const l of results.leads) {
      list.push({
        key: `l-${l.id}`,
        group: 'LEADS',
        icon: <LeadsIcon />,
        title: l.name,
        context: l.company,
        meta: l.status,
        run: () => go(`/leads/${l.id}`),
      });
    }
    for (const cr of results.crew) {
      list.push({
        key: `cr-${cr.id}`,
        group: 'CREW',
        icon: <CrewIcon />,
        title: cr.name,
        context: cr.company,
        meta: cr.role ?? undefined,
        run: () => go('/crew'),
      });
    }
    for (const ex of results.expenses) {
      list.push({
        key: `ex-${ex.id}`,
        group: 'EXPENSES',
        icon: <FinanceIcon />,
        title: ex.vendor || ex.description || ex.category,
        context: ex.description && ex.vendor ? ex.description : ex.category,
        meta: (
          <span className="gold">{formatMoney(displayCents(ex.amount_cents, ex.gst_cents, mode))}</span>
        ),
        run: () => go('/finance'),
      });
    }
    if (query.trim().length > 0) {
      list.push({
        key: 'new-task',
        group: 'ACTIONS',
        icon: <TasksIcon />,
        title: `New task: “${query.trim()}”`,
        meta: 'due today ↵',
        run: () => beginTask(query.trim()),
      });
      list.push({
        key: 'new-lead',
        group: 'ACTIONS',
        icon: <LeadsIcon />,
        title: `New lead: “${query.trim()}”`,
        meta: 'opens detail',
        run: () => createLead(query.trim()),
      });
    }
    return list;
  }, [results, query, taskTitle, composing, projects, mode, go, createTask, createLead, beginTask]);

  // Keep the highlighted row in view.
  useEffect(() => {
    listRef.current
      ?.querySelectorAll('.cp-item')
      [index]?.scrollIntoView({ block: 'nearest' });
  }, [index]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndex((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      items[index]?.run();
    } else if (e.key === 'Escape') {
      if (taskTitle !== null) {
        setTaskTitle(null);
        setQuery(taskTitle);
      } else {
        onClose();
      }
      e.stopPropagation();
    }
  };

  let lastGroup: string | null = null;

  const inputRow = (
    <div className="cp-input-row">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
        <circle cx="7" cy="7" r="4.5" />
        <path d="M10.5 10.5L14 14" />
      </svg>
      <input
        ref={inputRef}
        value={query}
        placeholder={
          taskTitle !== null
            ? 'Filter projects — Enter on the first row skips…'
            : composing
              ? 'Task title… ↵ to continue'
              : 'Search projects, tasks, clients, invoices, leads… or type a new task'
        }
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        autoFocus
      />
      <span className="modal-esc">ESC</span>
    </div>
  );

  const list = (
    <div className="cp-list" ref={listRef}>
      {notice ? (
        <div className="cp-notice">{notice}</div>
      ) : (
        <>
          {items.map((item, i) => {
            const header = item.group !== lastGroup ? item.group : null;
            lastGroup = item.group;
            return (
              <div key={item.key}>
                {header && <div className="cp-group">{header}</div>}
                <div
                  className={`cp-item${i === index ? ' active' : ''}`}
                  onMouseEnter={() => setIndex(i)}
                  onClick={() => item.run()}
                >
                  <span className="cp-icon">{item.icon}</span>
                  <span className="cp-title" title={item.title}>{item.title}</span>
                  {item.context && <span className="cp-context" title={item.context}>{item.context}</span>}
                  <span className="cp-spacer" />
                  {item.meta && <span className="cp-meta">{item.meta}</span>}
                </div>
              </div>
            );
          })}
          {items.length === 0 && (
            <div className="cp-empty">
              {composing
                ? 'Type the task title — it lands due today.'
                : query.trim().length < 2
                  ? 'Type to search — or type a task and press Enter on “New task”.'
                  : 'No matches.'}
            </div>
          )}
        </>
      )}
    </div>
  );

  // ≤880px the palette is a full sheet with the input pinned under a
  // "Search / Cancel" header (§0.4.7; mockup §1.2 F). Compose mode (⌘E, and
  // the quick-add sheet's "New task" flow) works inside it unchanged; the
  // keyboard-hint footer is dropped because there is no keyboard.
  if (isMobile) {
    return (
      <Sheet
        open
        onClose={onClose}
        variant="full"
        title="Search"
        rightAction={<SheetButton onClick={onClose}>Cancel</SheetButton>}
        pinned={<div className="cp-mobile-input">{inputRow}</div>}
        className="search-sheet"
      >
        {list}
      </Sheet>
    );
  }

  return (
    <div className="overlay palette-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="palette" role="dialog" aria-modal>
        {inputRow}
        {list}
        <div className="cp-foot">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>⌘E new task</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  );
}
